// src/components/views/admin/AdminTryoutPresetsView.tsx
/**
 * Admin view: generate, review, and activate the pre-baked Tryout example images.
 *
 * Each preset room (see PRESET_IMAGES) has 4 slots: two empty-room variants
 * (A/B) and two "modern"-staged variants (A/B). "Generieren" calls the
 * `tryout-preset-bake` Edge Function which runs Gemini on the original preset
 * photo and upserts draft rows (is_active=false). "Aktivieren" flips all rows
 * for that preset live so the anonymous Tryout funnel serves them instead of
 * generating on the fly (saving tokens/latency for first-time visitors).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Sparkles, CheckCircle2, Trash2, Upload, ImageUp, RotateCcw, AlertTriangle, Pencil, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PRESET_IMAGES } from '@/components/tryout/TryoutStep1Upload';
import { resolvePresets, sourceIsNewerThanBakes } from '@/lib/tryout/presetOverrides';
import type { PresetOverrideRow } from '@/lib/tryout/presetOverrides';
import { ROOMS } from '@/lib/roomConfig';

// Room types offered as a preset override — only those the staging pipeline
// supports (mirrors TRYOUT_ROOMS in TryoutStep2Configure).
const STAGING_ROOMS = Object.values(ROOMS).filter((r) => r.supportsStaging);

interface PresetAssetRow {
  id: string;
  preset_id: string;
  kind: 'empty' | 'staged';
  variant: 'A' | 'B';
  style: string;
  storage_path: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface SlotDef {
  key: string;
  label: string;
  kind: 'empty' | 'staged';
  variant: 'A' | 'B';
  style: string; // '' for empty, 'modern' for staged
}

const SLOTS: SlotDef[] = [
  { key: 'empty-A', label: 'Leer 1', kind: 'empty', variant: 'A', style: '' },
  { key: 'empty-B', label: 'Leer 2', kind: 'empty', variant: 'B', style: '' },
  { key: 'staged-A-modern', label: 'Staging Modern 1', kind: 'staged', variant: 'A', style: 'modern' },
  { key: 'staged-B-modern', label: 'Staging Modern 2', kind: 'staged', variant: 'B', style: 'modern' },
];

async function fetchPresetBase64(src: string): Promise<{ base64: string; mimeType: string }> {
  const res = await fetch(src);
  const blob = await res.blob();
  const dataUrl: string = await new Promise((resolve) => {
    const fr = new FileReader();
    fr.onloadend = () => resolve(fr.result as string);
    fr.readAsDataURL(blob);
  });
  return { base64: dataUrl.split(',')[1], mimeType: blob.type || 'image/webp' };
}

function publicUrlFor(storagePath: string): string {
  return supabase.storage.from('images').getPublicUrl(storagePath).data.publicUrl;
}

export function AdminTryoutPresetsView() {
  const [rowsByPreset, setRowsByPreset] = useState<Record<string, PresetAssetRow[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [removingRowId, setRemovingRowId] = useState<string | null>(null);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<PresetOverrideRow[]>([]);
  const [sourceBusyId, setSourceBusyId] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState<{ id: string; value: string } | null>(null);

  const fetchRows = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from('tryout_preset_assets')
      .select('*')
      .order('updated_at', { ascending: false });
    if (fetchError) throw fetchError;

    const grouped: Record<string, PresetAssetRow[]> = {};
    for (const row of (data ?? []) as PresetAssetRow[]) {
      grouped[row.preset_id] = [...(grouped[row.preset_id] ?? []), row];
    }
    setRowsByPreset(grouped);

    const { data: ovr } = await supabase
      .from('tryout_preset_overrides')
      .select('preset_id, label, source_path, source_updated_at, room');
    setOverrides((ovr ?? []) as PresetOverrideRow[]);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchRows()
      .catch((err) => setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen'))
      .finally(() => setIsLoading(false));
  }, [fetchRows]);

  const reload = async () => {
    try {
      await fetchRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
    }
  };

  const handleGenerate = async (presetId: string, src: string) => {
    setError(null);
    setGeneratingId(presetId);
    try {
      const { base64, mimeType } = await fetchPresetBase64(src);
      const { data: { session } } = await supabase.auth.getSession();

      const { data, error: invokeError } = await supabase.functions.invoke('tryout-preset-bake', {
        body: { presetId, originalBase64: base64, mimeType },
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      });

      if (invokeError) throw invokeError;
      if (!data?.success) throw new Error(data?.error ?? 'Generieren fehlgeschlagen');

      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generieren fehlgeschlagen');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleActivate = async (presetId: string) => {
    setError(null);
    setActivatingId(presetId);
    try {
      const { error: updateError } = await supabase
        .from('tryout_preset_assets')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('preset_id', presetId);
      if (updateError) throw updateError;
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktivieren fehlgeschlagen');
    } finally {
      setActivatingId(null);
    }
  };

  const handleRemove = async (row: PresetAssetRow) => {
    setError(null);
    setRemovingRowId(row.id);
    try {
      const { error: deleteError } = await supabase.from('tryout_preset_assets').delete().eq('id', row.id);
      if (deleteError) throw deleteError;
      // Best-effort storage cleanup — a stray object is harmless, a broken row isn't.
      await supabase.storage.from('images').remove([row.storage_path]);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Entfernen fehlgeschlagen');
    } finally {
      setRemovingRowId(null);
    }
  };

  // Manual upload — drop a hand-picked image (e.g. a real Staging-v2 render from
  // the main tool) into a slot instead of auto-generating it. Stored as a draft
  // (is_active=false) like a bake, so it still needs "Aktivieren".
  const handleUpload = async (presetId: string, slot: SlotDef, file: File) => {
    setError(null);
    setUploadingKey(`${presetId}:${slot.key}`);
    try {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const path = `tryout-presets/${presetId}/${slot.kind}-${slot.variant}${slot.style ? `-${slot.style}` : ''}-${crypto.randomUUID()}.${ext}`;
      const existing = (rowsByPreset[presetId] ?? []).find(
        (r) => r.kind === slot.kind && r.variant === slot.variant && r.style === slot.style,
      );
      const { error: upErr } = await supabase.storage
        .from('images')
        .upload(path, file, { contentType: file.type || 'image/png', upsert: false });
      if (upErr) throw upErr;
      const { error: dbErr } = await supabase.from('tryout_preset_assets').upsert(
        {
          preset_id: presetId,
          kind: slot.kind,
          variant: slot.variant,
          style: slot.style,
          storage_path: path,
          is_active: false,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'preset_id,kind,variant,style' },
      );
      if (dbErr) throw dbErr;
      if (existing && existing.storage_path !== path) {
        await supabase.storage.from('images').remove([existing.storage_path]);
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setUploadingKey(null);
    }
  };

  // Replace a preset's original source photo. Goes live immediately (no draft):
  // it's only the "before" image, no token cost.
  const handleSourceReplace = async (presetId: string, file: File) => {
    setError(null);
    setSourceBusyId(presetId);
    try {
      const ext = (file.name.split('.').pop() || 'webp').toLowerCase();
      const path = `tryout-presets/${presetId}/source-${crypto.randomUUID()}.${ext}`;
      const prev = overrides.find((o) => o.preset_id === presetId)?.source_path ?? null;
      const { error: upErr } = await supabase.storage
        .from('images')
        .upload(path, file, { contentType: file.type || 'image/webp', upsert: false });
      if (upErr) throw upErr;
      const nowIso = new Date().toISOString();
      const { error: dbErr } = await supabase.from('tryout_preset_overrides').upsert(
        { preset_id: presetId, source_path: path, source_updated_at: nowIso, updated_at: nowIso },
        { onConflict: 'preset_id' },
      );
      if (dbErr) throw dbErr;
      if (prev && prev !== path) await supabase.storage.from('images').remove([prev]);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Quelle ersetzen fehlgeschlagen');
    } finally {
      setSourceBusyId(null);
    }
  };

  // Reset the source to the hardcoded default (null the column, drop the file).
  const handleSourceReset = async (presetId: string) => {
    setError(null);
    setSourceBusyId(presetId);
    try {
      const prev = overrides.find((o) => o.preset_id === presetId)?.source_path ?? null;
      const { error: dbErr } = await supabase
        .from('tryout_preset_overrides')
        .update({ source_path: null, source_updated_at: null, updated_at: new Date().toISOString() })
        .eq('preset_id', presetId);
      if (dbErr) throw dbErr;
      if (prev) await supabase.storage.from('images').remove([prev]);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zurücksetzen fehlgeschlagen');
    } finally {
      setSourceBusyId(null);
    }
  };

  // Save a room-label override. Blank input → reset to the default label (null).
  const handleLabelSave = async (presetId: string, value: string) => {
    setError(null);
    const trimmed = value.trim();
    try {
      const { error: dbErr } = await supabase.from('tryout_preset_overrides').upsert(
        { preset_id: presetId, label: trimmed || null, updated_at: new Date().toISOString() },
        { onConflict: 'preset_id' },
      );
      if (dbErr) throw dbErr;
      setLabelDraft(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Label speichern fehlgeschlagen');
    }
  };

  // Save a room-type override. Choosing the preset's default room stores NULL
  // (no override) so the funnel falls back cleanly. Changing the room fixes the
  // downstream funnel — e.g. a Küche→Flur preset stops asking "Küche behalten?".
  const handleRoomSave = async (presetId: string, room: string) => {
    setError(null);
    const defaultRoom = PRESET_IMAGES.find((p) => p.id === presetId)?.room;
    try {
      const { error: dbErr } = await supabase.from('tryout_preset_overrides').upsert(
        { preset_id: presetId, room: room === defaultRoom ? null : room, updated_at: new Date().toISOString() },
        { onConflict: 'preset_id' },
      );
      if (dbErr) throw dbErr;
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Raumtyp speichern fehlgeschlagen');
    }
  };

  const toUrl = (p: string) => supabase.storage.from('images').getPublicUrl(p).data.publicUrl;
  const effectivePresets = resolvePresets(PRESET_IMAGES, overrides, toUrl);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Tryout-Beispielbilder</h2>
        <p className="text-sm text-muted-foreground">
          Vorab generierte Leer- und Staging-Beispiele für den anonymen Tryout-Funnel. Aktive Slots
          werden Besuchern direkt ausgeliefert und sparen Generierungs-Tokens; fehlende Slots fallen
          automatisch auf die Live-Generierung zurück. Pro Slot kannst du auch ein eigenes Bild
          hochladen (↥) — z.&nbsp;B. ein im Haupt-Tool erstelltes Staging — und danach aktivieren.
          Das Original-Foto (oben links je Raum) und das Label kannst du direkt ersetzen bzw.
          bearbeiten; ein ersetztes Foto ist sofort live.
        </p>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {effectivePresets.map((preset) => {
            const rows = rowsByPreset[preset.id] ?? [];
            const hasDraft = rows.some((r) => !r.is_active);
            const isGenerating = generatingId === preset.id;
            const isActivating = activatingId === preset.id;

            return (
              <Card key={preset.id}>
                <CardHeader className="flex flex-row items-center gap-3 space-y-0">
                  <div className="relative w-14 h-14 flex-shrink-0">
                    <img
                      src={preset.src}
                      alt={preset.label}
                      className="w-14 h-14 rounded-md object-cover border border-border"
                    />
                    <label
                      title="Original-Foto ersetzen"
                      className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground hover:text-primary cursor-pointer"
                    >
                      {sourceBusyId === preset.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <ImageUp className="w-3 h-3" />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleSourceReplace(preset.id, f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  <div className="flex-1 min-w-0">
                    {labelDraft?.id === preset.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          autoFocus
                          value={labelDraft.value}
                          onChange={(e) => setLabelDraft({ id: preset.id, value: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleLabelSave(preset.id, labelDraft.value);
                            if (e.key === 'Escape') setLabelDraft(null);
                          }}
                          placeholder={PRESET_IMAGES.find((p) => p.id === preset.id)?.label}
                          className="h-7 text-sm px-2 rounded border border-border bg-background w-full min-w-0"
                        />
                        <button
                          type="button"
                          title="Speichern"
                          onClick={() => handleLabelSave(preset.id, labelDraft.value)}
                          className="w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-primary"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        title="Label bearbeiten"
                        onClick={() => setLabelDraft({ id: preset.id, value: preset.label })}
                        className="group flex items-center gap-1.5 text-left"
                      >
                        <CardTitle className="text-base">{preset.label}</CardTitle>
                        <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
                      </button>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-muted-foreground truncate">{preset.src}</p>
                      {overrides.find((o) => o.preset_id === preset.id)?.source_path && (
                        <button
                          type="button"
                          onClick={() => handleSourceReset(preset.id)}
                          disabled={sourceBusyId === preset.id}
                          title="Standard-Foto wiederherstellen"
                          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary disabled:opacity-50"
                        >
                          <RotateCcw className="w-3 h-3" />
                          Standard
                        </button>
                      )}
                    </div>
                    {sourceIsNewerThanBakes(
                      overrides.find((o) => o.preset_id === preset.id)?.source_updated_at ?? null,
                      rows.map((r) => r.updated_at),
                    ) && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-600">
                        <AlertTriangle className="w-3 h-3" />
                        Quelle geändert – Beispiele veraltet, neu generieren
                      </div>
                    )}
                    {/* Room type — drives the funnel (e.g. Küche→Flur removes the
                        "Küche behalten?" question). Picking the default stores no override. */}
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <span className="text-[11px] text-muted-foreground">Raumtyp</span>
                      <select
                        value={preset.room}
                        onChange={(e) => handleRoomSave(preset.id, e.target.value)}
                        className="h-7 text-xs rounded-md border border-border bg-background px-2 text-foreground max-w-[170px]"
                      >
                        {STAGING_ROOMS.map((r) => (
                          <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                      </select>
                      {overrides.find((o) => o.preset_id === preset.id)?.room && (
                        <span className="text-[10px] text-amber-600">abweichend</span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {SLOTS.map((slot) => {
                      const row = rows.find(
                        (r) => r.kind === slot.kind && r.variant === slot.variant && r.style === slot.style,
                      );
                      return (
                        <div key={slot.key} className="space-y-1">
                          <div className="relative aspect-square rounded-md border border-border bg-muted/30 overflow-hidden">
                            {row ? (
                              <img
                                src={publicUrlFor(row.storage_path)}
                                alt={slot.label}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground text-center px-1">
                                kein Bild
                              </div>
                            )}
                            <label
                              title="Eigenes Bild hochladen"
                              className="absolute top-1 left-1 w-6 h-6 rounded-full bg-background/90 border border-border flex items-center justify-center text-muted-foreground hover:text-primary cursor-pointer"
                            >
                              {uploadingKey === `${preset.id}:${slot.key}` ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Upload className="w-3 h-3" />
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) handleUpload(preset.id, slot, f);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                            {row && (
                              <button
                                type="button"
                                onClick={() => handleRemove(row)}
                                disabled={removingRowId === row.id}
                                title="Entfernen"
                                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-background/90 border border-border flex items-center justify-center text-muted-foreground hover:text-destructive disabled:opacity-50"
                              >
                                {removingRowId === row.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3 h-3" />
                                )}
                              </button>
                            )}
                          </div>
                          <p className="text-[11px] font-medium truncate">{slot.label}</p>
                          <Badge
                            variant="outline"
                            className={
                              row?.is_active
                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600'
                                : row
                                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-600'
                                  : 'border-muted text-muted-foreground'
                            }
                          >
                            {row?.is_active ? 'aktiv' : row ? 'Entwurf' : 'leer · Live'}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleGenerate(preset.id, preset.src)}
                      disabled={generatingId !== null}
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          Generiere… (~1–2 Min)
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                          Generieren
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleActivate(preset.id)}
                      disabled={!hasDraft || isActivating}
                    >
                      {isActivating ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Aktivieren
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
